import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminInbox from "@/pages/AdminInbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  title: "",
  role: "",
  category: "Feature Film",
  status: "In Development",
  year: "",
  logline: "",
  description: "",
  image_url: "",
};

const CATEGORIES = ["Feature Film", "Rom Com", "TV Series", "Short Series", "Limited Series", "Series", "Feature Documentary", "Short Film", "Play"];
const STATUSES = ["In Development", "In Pre-Production", "In Production", "In Post-Production", "Released"];

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("slate");
  const [unread, setUnread] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch {
      toast.error("Failed to load projects.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get("/messages")
      .then(({ data }) => setUnread(data.filter((m) => !m.read).length))
      .catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title || "",
      role: p.role || "",
      category: p.category || "Feature Film",
      status: p.status || "In Development",
      year: p.year || "",
      logline: p.logline || "",
      description: p.description || "",
      image_url: p.image_url || "",
    });
    setDialogOpen(true);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title || !form.role) {
      toast.error("Title and role are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/projects/${editing.id}`, form);
        toast.success("Project updated.");
      } else {
        await api.post("/projects", form);
        toast.success("Project added.");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/projects/${deleteTarget.id}`);
      toast.success("Project removed.");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed.");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-alabaster font-sans text-onyx">
      <header className="border-b border-[#DCDEE6] bg-[rgba(244,244,247,0.8)] backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="font-serif text-xl">
            Onyx <span className="text-ochre">&amp;</span> Ochre
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-onyx-muted ml-3">
              Studio
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-view-site"
              className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-onyx-muted hover:text-ochre"
            >
              View site <ExternalLink className="w-3 h-3" />
            </a>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-onyx-muted hover:text-ochre"
            >
              <LogOut className="w-3 h-3" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="font-serif text-4xl">Studio Dashboard</h1>
          <p className="text-onyx-muted mt-2 text-sm">Signed in as {user?.email}.</p>
        </div>

        <div className="flex items-center justify-between border-b border-[#DCDEE6] mb-8">
          <div className="flex gap-8">
            <button
              onClick={() => setTab("slate")}
              data-testid="tab-slate"
              className={`pb-3 font-mono text-xs tracking-[0.2em] uppercase transition-colors border-b-2 -mb-px ${
                tab === "slate" ? "border-ochre text-onyx" : "border-transparent text-onyx-muted hover:text-onyx"
              }`}
            >
              Current Slate
            </button>
            <button
              onClick={() => setTab("inbox")}
              data-testid="tab-inbox"
              className={`pb-3 font-mono text-xs tracking-[0.2em] uppercase transition-colors border-b-2 -mb-px flex items-center gap-2 ${
                tab === "inbox" ? "border-ochre text-onyx" : "border-transparent text-onyx-muted hover:text-onyx"
              }`}
            >
              Inbox
              {unread > 0 && (
                <span
                  data-testid="inbox-unread-badge"
                  className="font-mono text-[9px] bg-ochre text-alabaster px-1.5 py-0.5 rounded-full"
                >
                  {unread}
                </span>
              )}
            </button>
          </div>
          {tab === "slate" && (
            <button
              onClick={openCreate}
              data-testid="admin-add-project"
              className="flex items-center gap-2 font-sans uppercase text-xs tracking-[0.2em] bg-onyx text-alabaster px-6 py-3 hover:bg-ochre transition-colors mb-3"
            >
              <Plus className="w-4 h-4" /> Add project
            </button>
          )}
        </div>

        {tab === "inbox" ? (
          <AdminInbox onUnreadChange={setUnread} />
        ) : loading ? (
          <div className="py-20 text-center font-mono text-sm text-onyx-muted">Loading…</div>
        ) : (
          <div className="border border-[#DCDEE6] bg-white/40" data-testid="admin-project-table">
            <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#DCDEE6] font-mono text-[10px] tracking-[0.2em] uppercase text-onyx-muted">
              <div className="col-span-4">Title</div>
              <div className="col-span-3">Role</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {projects.length === 0 ? (
              <div className="px-5 py-12 text-center text-onyx-muted">No projects yet.</div>
            ) : (
              projects.map((p) => (
                <div
                  key={p.id}
                  data-testid={`admin-row-${p.id}`}
                  className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-[#E7E9F0] items-center text-sm"
                >
                  <div className="col-span-4 font-serif text-lg">{p.title}</div>
                  <div className="col-span-3 text-onyx-muted">{p.role}</div>
                  <div className="col-span-2 text-onyx-muted">{p.category}</div>
                  <div className="col-span-2">
                    <span className="font-mono text-[10px] tracking-wider uppercase text-ochre">
                      {p.status}
                    </span>
                  </div>
                  <div className="col-span-1 flex items-center justify-end gap-3">
                    <button
                      onClick={() => openEdit(p)}
                      data-testid={`admin-edit-${p.id}`}
                      className="text-onyx-muted hover:text-ochre"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(p)}
                      data-testid={`admin-delete-${p.id}`}
                      className="text-onyx-muted hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-alabaster max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editing ? "Edit project" : "Add project"}
            </DialogTitle>
            <DialogDescription className="font-sans text-sm text-onyx-muted">
              Fill in the details below. Title and role are required.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Title">
              <input className="admin-input" data-testid="dialog-title" value={form.title} onChange={set("title")} />
            </Field>
            <Field label="Role">
              <input className="admin-input" data-testid="dialog-role" value={form.role} onChange={set("role")} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Category">
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="dialog-category" className="rounded-none border-[#CFD3DE]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="dialog-status" className="rounded-none border-[#CFD3DE]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Year">
              <input className="admin-input" data-testid="dialog-year" value={form.year} onChange={set("year")} />
            </Field>
            <Field label="Logline">
              <input className="admin-input" data-testid="dialog-logline" value={form.logline} onChange={set("logline")} />
            </Field>
            <Field label="Description">
              <textarea className="admin-input resize-none" rows={3} data-testid="dialog-description" value={form.description} onChange={set("description")} />
            </Field>
            <Field label="Image URL">
              <input className="admin-input" data-testid="dialog-image" value={form.image_url} onChange={set("image_url")} />
            </Field>
          </div>
          <DialogFooter>
            <button
              onClick={save}
              disabled={saving}
              data-testid="dialog-save"
              className="font-sans uppercase text-xs tracking-[0.2em] bg-onyx text-alabaster px-8 py-3 hover:bg-ochre transition-colors disabled:opacity-50"
            >
              {saving ? "Saving…" : editing ? "Save changes" : "Add project"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-alabaster" data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Remove project?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{deleteTarget?.title}&rdquo; will be permanently removed from your slate. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel" className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="delete-confirm"
              className="rounded-none bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-onyx-muted block mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
